/**
 * Transparency score — how complete and checkable a creator profile is.
 *
 * Design constraint that comes straight from the brand promise: this score must
 * be fully explainable. It is a flat, deterministic sum of published criteria,
 * and `scoreCreatorProfile` returns the per-criterion breakdown so the UI can
 * show a creator exactly what they're missing and exactly what it's worth.
 *
 * What this score is NOT:
 *   - It is not a quality or performance ranking.
 *   - It is not for sale. Nothing a creator or brand can pay for moves it.
 *   - It does not treat self-reported audience numbers as verified. Completing
 *     a stats field earns points for *disclosure*; the `verified` criteria are
 *     separate and only granted by an actual platform link or a completed
 *     partnership on Affily.
 */

/**
 * Criteria are intentionally a flat list, in display order.
 * `weight` values are points out of 100 and must sum to exactly 100 — asserted
 * below so the list can't silently drift out of alignment.
 */
export const TRANSPARENCY_CRITERIA = [
  {
    key: 'display_name',
    label: 'Display name and headline',
    weight: 5,
    hint: 'Add your name and a one-line description of what you make.',
    test: (p) => Boolean(p.displayName?.trim() && p.headline?.trim()),
  },
  {
    key: 'bio',
    label: 'Bio (60+ characters)',
    weight: 5,
    hint: 'Write at least a couple of sentences about your work.',
    test: (p) => (p.bio?.trim().length ?? 0) >= 60,
  },
  {
    key: 'avatar',
    label: 'Profile photo',
    weight: 5,
    hint: 'Upload a profile photo.',
    test: (p) => Boolean(p.avatarUrl),
  },
  {
    key: 'niches',
    label: 'At least one niche',
    weight: 5,
    hint: 'Tag the categories you create in.',
    test: (p) => (p.niches?.length ?? 0) >= 1,
  },
  {
    key: 'platform_linked',
    label: 'At least one platform linked',
    weight: 12,
    hint: 'Link a public profile URL so anyone can check your numbers themselves.',
    test: (p) => (p.platforms ?? []).some((pl) => Boolean(pl.url)),
  },
  {
    key: 'platforms_all_linked',
    label: 'Every listed platform has a public URL',
    weight: 8,
    hint: 'Add a public URL to each platform you list — unlinked stats can’t be checked.',
    test: (p) => (p.platforms?.length ?? 0) > 0 && p.platforms.every((pl) => Boolean(pl.url)),
  },
  {
    key: 'audience_size',
    label: 'Follower counts on every platform',
    weight: 8,
    hint: 'Report follower counts for each platform you list.',
    test: (p) =>
      (p.platforms?.length ?? 0) > 0 &&
      p.platforms.every((pl) => Number.isFinite(pl.followers) && pl.followers > 0),
  },
  {
    key: 'engagement',
    label: 'Avg views and engagement rate',
    weight: 10,
    hint: 'Report average views and engagement rate — the numbers brands actually price against.',
    test: (p) =>
      (p.platforms?.length ?? 0) > 0 &&
      p.platforms.every(
        (pl) =>
          Number.isFinite(pl.avgViews) &&
          pl.avgViews > 0 &&
          Number.isFinite(pl.engagementRateBps) &&
          pl.engagementRateBps > 0,
      ),
  },
  {
    key: 'demographics_countries',
    label: 'Top audience countries',
    weight: 8,
    hint: 'Add your top audience countries and their share.',
    test: (p) => (p.demographics?.topCountries?.length ?? 0) >= 1,
  },
  {
    key: 'demographics_age',
    label: 'Audience age split',
    weight: 7,
    hint: 'Add your audience age breakdown.',
    test: (p) => (p.demographics?.ageSplit?.length ?? 0) >= 1,
  },
  {
    key: 'starting_rates',
    label: 'Public starting rates',
    weight: 12,
    hint: 'Publish your starting rates. This is the whole point of Affily — no “DM for pricing”.',
    test: (p) => (p.startingRates?.length ?? 0) >= 1,
  },
  {
    key: 'portfolio',
    label: 'UGC portfolio (2+ examples)',
    weight: 8,
    hint: 'Upload at least two examples of your work.',
    test: (p) => (p.portfolio?.length ?? 0) >= 2,
  },
  {
    key: 'past_collaborations',
    label: 'Past collaborations listed',
    weight: 4,
    hint: 'List brands you’ve worked with.',
    test: (p) => (p.pastCollaborations?.length ?? 0) >= 1,
  },
  {
    key: 'completed_partnership',
    label: 'A completed partnership on Affily',
    weight: 3,
    hint: 'Complete a partnership on Affily — its tracked numbers are verified, not self-reported.',
    test: (p) => (p.completedPartnershipCount ?? 0) >= 1,
  },
];

const TOTAL_WEIGHT = TRANSPARENCY_CRITERIA.reduce((sum, c) => sum + c.weight, 0);
if (TOTAL_WEIGHT !== 100) {
  throw new Error(`Transparency criteria weights must sum to 100, got ${TOTAL_WEIGHT}`);
}

export const TRANSPARENCY_TIERS = [
  { key: 'exemplary', label: 'Exemplary', min: 90 },
  { key: 'high', label: 'High', min: 70 },
  { key: 'moderate', label: 'Moderate', min: 45 },
  { key: 'low', label: 'Low', min: 0 },
];

export function transparencyTier(score) {
  return TRANSPARENCY_TIERS.find((t) => score >= t.min) ?? TRANSPARENCY_TIERS.at(-1);
}

/**
 * Score a creator profile 0–100 with a full, per-criterion explanation.
 *
 * @param {object} profile A CreatorProfile-shaped object (plain or Mongoose doc).
 * @returns {{score: number, tier: object, earned: number, criteria: Array}}
 */
export function scoreCreatorProfile(profile) {
  const p = profile ?? {};

  const criteria = TRANSPARENCY_CRITERIA.map((c) => {
    let met = false;
    try {
      met = Boolean(c.test(p));
    } catch {
      // A malformed field means the criterion simply isn't met. Scoring must
      // never be the thing that takes a profile page down.
      met = false;
    }
    return { key: c.key, label: c.label, weight: c.weight, hint: c.hint, met };
  });

  const earned = criteria.reduce((sum, c) => sum + (c.met ? c.weight : 0), 0);
  const score = Math.round((earned / TOTAL_WEIGHT) * 100);

  return { score, tier: transparencyTier(score), earned, criteria };
}

/** The unmet criteria, richest-first — this is the profile editor's to-do list. */
export function missingCriteria(profile) {
  return scoreCreatorProfile(profile)
    .criteria.filter((c) => !c.met)
    .sort((a, b) => b.weight - a.weight);
}
